/**
 * Concept playbook vocabulary for the web app.
 *
 * MIRROR of `packages/shared/src/stat-option-catalog.ts` (the web bundle does
 * not import `@gridiron/shared`). `stat-catalog.mirror.test.ts` fails the
 * build if the two drift, so edit the shared file first and copy here.
 *
 * Terminology (Terminology Notes 9/21/26): a **Stat** is one aspect of a play
 * (Offensive Formation…); an **Option** is one value of a stat (Trips…); a
 * **Stock option** ships with every team; a **Concept** is the designed
 * intention of a playcall; a **Playcall** is the combination of options picked
 * on Play Entry.
 */
import type {
  PlaybookUnit,
  PlayEntryStatCardView,
  StatCategory,
  StatDefinitionView,
  StatSide,
} from '../../lib/api-types';

export type { PlaybookUnit, StatCategory, StatSide };

export const STAT_DEFINITIONS: readonly StatDefinitionView[] = [
  {
    category: 'OFF_CONCEPT',
    label: 'Offensive Concept',
    short_label: 'Concept',
    unit: 'offense',
    sides: ['own', 'opponent'],
    definition:
      'The strategic design of the intention of the offensive playcall as designed on paper. It can be run out of different looks, alignments and formations.',
  },
  {
    category: 'FORMATION',
    label: 'Offensive Formation',
    short_label: 'Formation',
    unit: 'offense',
    sides: ['own', 'opponent'],
    definition:
      'Alignment of the offense before the snap of the ball — where the offensive skill players line up in the play design.',
  },
  {
    category: 'PERSONNEL',
    label: 'Offensive Personnel',
    short_label: 'Personnel',
    unit: 'offense',
    sides: ['own', 'opponent'],
    definition:
      'Grouping of offensive players on the field to execute a play, usually two digits: running backs then tight ends (12 = 1 RB, 2 TE). Teams may also name custom groups.',
  },
  {
    category: 'MOTION',
    label: 'Offensive Motion',
    short_label: 'Motion',
    unit: 'offense',
    sides: ['own', 'opponent'],
    definition:
      'Offensive skill players moving before the ball is snapped. Some motions are common; many teams document unique ones.',
  },
  {
    category: 'PROTECTION',
    label: 'Offensive Protection',
    short_label: 'Protection',
    unit: 'offense',
    sides: ['own', 'opponent'],
    definition:
      'How the offensive line and supporting skill players block on a given play. Each team names its own protections.',
  },
  {
    category: 'DEF_CONCEPT',
    label: 'Defensive Concept',
    short_label: 'Concept',
    unit: 'defense',
    sides: ['own', 'opponent'],
    definition:
      'The strategic design of the intention of the defensive playcall as designed on paper. It can be run out of different fronts, blitzes and coverages.',
  },
  {
    category: 'FRONT',
    label: 'Defensive Front',
    short_label: 'Front',
    unit: 'defense',
    sides: ['own', 'opponent'],
    definition:
      'Alignment of the defensive linemen and linebackers before the snap of the ball. Standard fronts are common; most defenses also name custom ones.',
  },
  {
    category: 'COVERAGE',
    label: 'Defensive Coverage',
    short_label: 'Coverage',
    unit: 'defense',
    sides: ['own', 'opponent'],
    definition:
      'Final alignment and area of responsibility of the defensive backs after the snap of the ball.',
  },
  {
    category: 'BLITZ',
    label: 'Defensive Blitz',
    short_label: 'Blitz',
    unit: 'defense',
    sides: ['own', 'opponent'],
    definition:
      'A defensive attack that sends more than the normal number of front players at the quarterback. Each team names its own blitzes.',
  },
  {
    category: 'ST_CONCEPT',
    label: 'ST Concept',
    short_label: 'Concept',
    unit: 'special_teams',
    sides: ['own', 'opponent'],
    definition: 'The designed intention of a special-teams playcall (kick, punt, return, fake).',
  },
  {
    category: 'ST_FORMATION',
    label: 'ST Formation',
    short_label: 'Formation',
    unit: 'special_teams',
    sides: ['own', 'opponent'],
    definition: 'Alignment of the kicking or receiving unit before the snap.',
  },
  {
    category: 'ST_PERSONNEL',
    label: 'ST Personnel',
    short_label: 'Personnel',
    unit: 'special_teams',
    sides: ['own', 'opponent'],
    definition: 'The special-teams unit on the field for the play.',
  },
  {
    category: 'ST_PROTECTION',
    label: 'ST Protection',
    short_label: 'Protection',
    unit: 'special_teams',
    sides: ['own', 'opponent'],
    definition: 'How the kicking unit protects the kicker or punter.',
  },
  {
    category: 'ST_COVERAGE_SCHEME',
    label: 'Coverage Scheme',
    short_label: 'Cov. scheme',
    unit: 'special_teams',
    sides: ['own', 'opponent'],
    definition: 'How the kicking unit covers the kick and contains the returner.',
  },
  {
    category: 'ST_RETURN_CONCEPT',
    label: 'Return Concept',
    short_label: 'Return',
    unit: 'special_teams',
    sides: ['own', 'opponent'],
    definition: 'The designed return — wall, wedge, middle, sideline — for the receiving unit.',
  },
  {
    category: 'ST_RUSH_BLOCK_SCHEME',
    label: 'Rush / Block Scheme',
    short_label: 'Rush / block',
    unit: 'special_teams',
    sides: ['own', 'opponent'],
    definition: 'How the receiving unit attacks the kick to block it or pressure the kicker.',
  },
  {
    category: 'ST_OPPONENT_ALIGNMENT',
    label: 'Opponent Alignment',
    short_label: 'Opp. alignment',
    unit: 'special_teams',
    sides: ['opponent'],
    definition: "The opposing special-teams unit's alignment against our kick or return.",
  },
];

const BY_CATEGORY = new Map(STAT_DEFINITIONS.map((d) => [d.category, d]));

/**
 * Total over `StatCategory`: every member is in STAT_DEFINITIONS (the mirror
 * test proves it), so the non-null assertion never fires at runtime.
 */
export function statDefinition(category: StatCategory): StatDefinitionView {
  return BY_CATEGORY.get(category)!;
}

export const PLAYBOOK_TABS_BY_UNIT: Readonly<Record<PlaybookUnit, readonly StatCategory[]>> = {
  offense: [
    'OFF_CONCEPT',
    'FORMATION',
    'PERSONNEL',
    'MOTION',
    'PROTECTION',
    'FRONT',
    'COVERAGE',
    'BLITZ',
    'DEF_CONCEPT',
  ],
  defense: [
    'DEF_CONCEPT',
    'FRONT',
    'COVERAGE',
    'BLITZ',
    'OFF_CONCEPT',
    'FORMATION',
    'PERSONNEL',
    'MOTION',
    'PROTECTION',
  ],
  special_teams: [
    'ST_CONCEPT',
    'ST_FORMATION',
    'ST_PERSONNEL',
    'ST_PROTECTION',
    'ST_COVERAGE_SCHEME',
    'ST_RETURN_CONCEPT',
    'ST_RUSH_BLOCK_SCHEME',
    'ST_OPPONENT_ALIGNMENT',
  ],
};

const card = (
  id: string,
  field: string,
  category: StatCategory,
  side: StatSide,
  phase: PlaybookUnit,
  title: string,
  label: string
): PlayEntryStatCardView => ({ id, field, category, side, phase, title, label });

/* prettier-ignore */
export const PLAY_ENTRY_STAT_CARDS: Readonly<Record<PlaybookUnit, readonly PlayEntryStatCardView[]>> = {
  offense: [
    card('offConcept', 'offensive_concept', 'OFF_CONCEPT', 'own', 'offense', 'Concept', 'Offensive concept'),
    card('formation', 'offensive_formation', 'FORMATION', 'own', 'offense', 'Formation', 'Offensive formation'),
    card('personnel', 'offensive_personnel', 'PERSONNEL', 'own', 'offense', 'Personnel', 'Offensive personnel'),
    card('motion', 'motion', 'MOTION', 'own', 'offense', 'Motion', 'Offensive motion'),
    card('protection', 'protection', 'PROTECTION', 'own', 'offense', 'Protection', 'Offensive protection'),
    card('oppFront', 'defensive_front', 'FRONT', 'opponent', 'offense', 'Opp. front', "Opponent's defensive front"),
    card('oppCoverage', 'defensive_coverage', 'COVERAGE', 'opponent', 'offense', 'Opp. coverage', "Opponent's defensive coverage"),
    card('oppBlitz', 'blitz_call', 'BLITZ', 'opponent', 'offense', 'Opp. blitz', "Opponent's blitz"),
    card('oppDefConcept', 'defensive_concept', 'DEF_CONCEPT', 'opponent', 'offense', 'Opp. def. concept', "Opponent's defensive concept"),
  ],
  defense: [
    card('defConcept', 'defensive_concept', 'DEF_CONCEPT', 'own', 'defense', 'Concept', 'Defensive concept'),
    card('defFront', 'defensive_front', 'FRONT', 'own', 'defense', 'Front', 'Defensive front'),
    card('defCoverage', 'defensive_coverage', 'COVERAGE', 'own', 'defense', 'Coverage', 'Defensive coverage'),
    card('defBlitz', 'blitz_call', 'BLITZ', 'own', 'defense', 'Blitz', 'Defensive blitz'),
    card('oppOffConcept', 'offensive_concept', 'OFF_CONCEPT', 'opponent', 'defense', 'Opp. concept', "Opponent's offensive concept"),
    card('oppFormation', 'offensive_formation', 'FORMATION', 'opponent', 'defense', 'Opp. formation', "Opponent's offensive formation"),
    card('oppPersonnel', 'offensive_personnel', 'PERSONNEL', 'opponent', 'defense', 'Opp. personnel', "Opponent's offensive personnel"),
    card('oppMotion', 'motion', 'MOTION', 'opponent', 'defense', 'Opp. motion', "Opponent's motion"),
    card('oppProtection', 'protection', 'PROTECTION', 'opponent', 'defense', 'Opp. protection', "Opponent's protection"),
  ],
  special_teams: [
    card('stConcept', 'st_concept', 'ST_CONCEPT', 'own', 'special_teams', 'Concept', 'ST concept'),
    card('stFormation', 'st_formation', 'ST_FORMATION', 'own', 'special_teams', 'Formation', 'ST formation'),
    card('stPersonnel', 'st_personnel', 'ST_PERSONNEL', 'own', 'special_teams', 'Personnel', 'ST personnel'),
    card('stProtection', 'st_protection', 'ST_PROTECTION', 'own', 'special_teams', 'Protection', 'ST protection'),
    card('stCoverageScheme', 'st_coverage_scheme', 'ST_COVERAGE_SCHEME', 'own', 'special_teams', 'Cov. scheme', 'Coverage scheme'),
    card('stReturnConcept', 'st_return_concept', 'ST_RETURN_CONCEPT', 'own', 'special_teams', 'Return', 'Return concept'),
    card('stRushBlockScheme', 'st_rush_block_scheme', 'ST_RUSH_BLOCK_SCHEME', 'own', 'special_teams', 'Rush / block', 'Rush / block scheme'),
    card('stOpponentAlignment', 'st_opponent_alignment', 'ST_OPPONENT_ALIGNMENT', 'opponent', 'special_teams', 'Opp. alignment', 'Opponent alignment'),
  ],
};

/**
 * Design type of each stock offensive concept (owner ruling 2026-09-29). None is
 * an RPO on its own: an RPO is a run piece + a pass piece combined on a play.
 * Migration 0045 applies the same map to existing teams, both sides.
 */
export const STOCK_CONCEPT_DESIGN_TYPES: Readonly<Record<string, 'run' | 'pass'>> = {
  'Inside-Zone': 'run',
  'Outside-Zone': 'run',
  Power: 'run',
  Counter: 'run',
  Dive: 'run',
  Jet: 'run',
  Draw: 'run',
  'QB-Sneak': 'run',
  Trap: 'run',
  Screen: 'pass',
  Bubble: 'pass',
  Curl: 'pass',
  Slant: 'pass',
  Smash: 'pass',
  Verticals: 'pass',
  Hitch: 'pass',
};

/**
 * Whose option list a Playbook tab edits. Never chosen by the coach: it is
 * the side the unit's Play Entry card for that stat reads, so the Playbook
 * always edits exactly what Play Entry offers. On Offense the offensive stats
 * are ours and the defensive stats the opponent's; on Defense the reverse;
 * Special Teams are all ours except Opponent Alignment.
 */
export function playbookSide(unit: PlaybookUnit, category: StatCategory): StatSide {
  const card = PLAY_ENTRY_STAT_CARDS[unit].find((c) => c.category === category);
  if (card !== undefined) return card.side;
  // Not a tab of this unit (a hand-edited URL): fall back to the stat's own
  // unit — ours when it belongs to this unit, otherwise the opponent's.
  const definition = statDefinition(category);
  const preferred: StatSide = definition.unit === unit ? 'own' : 'opponent';
  return definition.sides.includes(preferred) ? preferred : definition.sides[0]!;
}

export function statCardFor(phase: PlaybookUnit, field: string): PlayEntryStatCardView | undefined {
  return PLAY_ENTRY_STAT_CARDS[phase].find((c) => c.field === field);
}

export function statCardById(phase: PlaybookUnit, id: string): PlayEntryStatCardView | undefined {
  return PLAY_ENTRY_STAT_CARDS[phase].find((c) => c.id === id);
}

/** Every catalog wire field across phases. */
export const PLAYCALL_FIELDS: readonly string[] = [
  ...new Set(
    (Object.keys(PLAY_ENTRY_STAT_CARDS) as PlaybookUnit[]).flatMap((phase) =>
      PLAY_ENTRY_STAT_CARDS[phase].map((c) => c.field)
    )
  ),
];

export const PLAY_DIRECTIONS = ['left', 'middle', 'right'] as const;
export const PLAY_DIRECTION_LABELS: Readonly<Record<(typeof PLAY_DIRECTIONS)[number], string>> = {
  left: 'Left',
  middle: 'Middle',
  right: 'Right',
};

export const UNIT_LABELS: Readonly<Record<PlaybookUnit, string>> = {
  offense: 'Offense',
  defense: 'Defense',
  special_teams: 'Special Teams',
};

export const SIDE_LABELS: Readonly<Record<StatSide, string>> = {
  own: 'Our team',
  opponent: 'Opponent',
};
